import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import Icon from '@mdi/react'
import {
  mdiShareVariant,
  mdiCamera,
  mdiZipBox,
  mdiImageMultiple,
  mdiHospitalBox,
  mdiClose,
  mdiDownload,
} from '@mdi/js'
import { t } from '../i18n'
import { getPwaInstallShareHint } from '../utils/pwaInstallPrompt'
import {
  formatBytes,
  SHARE_ZIP_WARN_BYTES,
  SHARE_ZIP_BLOCK_BYTES,
} from '../utils/sharePayload'
import '../skins/capture.css'
import '../skins/share.css'
import StudyShareLinkSection from './StudyShareLinkSection'

/**
 * Sheet de contenido a compartir (classic / edge / studio).
 */
function ShareSheet({
  open,
  skin = 'classic',
  busy = false,
  status = '',
  zipInfo = null,
  canZip = false,
  zipPendingLabel = '',
  canPreviews = false,
  canPacs = false,
  portalMode = false,
  pacsDestId = 'default',
  pacsDestOptions = [],
  onPacsDestChange,
  onManageScpDest,
  onClose,
  onShareCapture,
  onShareZip,
  onDownloadZip,
  onShareSeriesPreviews,
  onShareStudyPreviews,
  onStowCurrent,
  onStowSeries,
  studyShareUrl,
  onCopyStudyLink,
  onShareStudyLink,
  pwaInstallHint = false,
  onInstallPwa,
  onDismissPwaInstall,
}) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose && onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, busy, onClose])

  if (!open) return null

  const tier = zipInfo && zipInfo.tier
  const zipSizeLabel =
    zipInfo && zipInfo.byteLength != null
      ? formatBytes(zipInfo.byteLength)
      : null
  const zipBlocked = tier === 'block'
  const zipWarn = tier === 'warn'

  const node = (
    <div
      className={`udv-share-root udv-capture--${skin}`}
      role="presentation"
      onClick={() => {
        if (!busy && onClose) onClose()
      }}
    >
      <div
        className="udv-share-card"
        role="dialog"
        aria-label={t('share.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="udv-capture-head">
          <span className="udv-capture-title">
            <Icon
              path={mdiShareVariant}
              size={0.55}
              color="currentColor"
              style={{ marginRight: 6, verticalAlign: '-2px' }}
            />
            {t('share.title')}
          </span>
          <button
            type="button"
            className="udv-capture-x"
            onClick={onClose}
            disabled={busy}
            aria-label={t('common.close')}
          >
            <Icon path={mdiClose} size={0.7} color="currentColor" />
          </button>
        </div>

        {portalMode ? (
          <div className="udv-share-portal-info" role="note">
            {t('share.portalDiagnosticZipHint')}
          </div>
        ) : null}

        <div className="udv-share-section">
          <div className="udv-share-section-label">{t('share.sectionContent')}</div>
          <div className="udv-share-actions">
            <button
              type="button"
              className={`udv-share-btn${busy ? ' is-busy' : ''}`}
              disabled={busy}
              onClick={() => onShareCapture && onShareCapture()}
            >
              <Icon path={mdiCamera} size={0.9} color="currentColor" />
              <span className="udv-share-btn-label">{t('share.capture')}</span>
              <span className="udv-share-btn-sub">
                {portalMode ? t('share.portalCaptureSub') : t('share.captureSub')}
              </span>
            </button>
            <button
              type="button"
              className={`udv-share-btn${busy ? ' is-busy' : ''}${portalMode && canZip ? ' is-portal-zip-highlight' : ''}`}
              disabled={busy || !canZip || zipBlocked}
              onClick={() => onShareZip && onShareZip()}
            >
              <Icon path={mdiZipBox} size={0.9} color="currentColor" />
              <span className="udv-share-btn-label">{t('share.zip')}</span>
              <span className="udv-share-btn-sub">
                {zipSizeLabel
                  ? zipSizeLabel
                  : canZip
                    ? portalMode
                      ? t('share.portalZipSub')
                      : t('share.zipSub')
                    : portalMode
                      ? zipPendingLabel || t('share.portalZipPending')
                      : zipPendingLabel || t('share.zipUnavailable')}
              </span>
            </button>
          </div>
          {zipBlocked ? (
            <div className="udv-share-warn">
              {t('share.zipBlocked', {
                size: zipSizeLabel || formatBytes(SHARE_ZIP_BLOCK_BYTES),
              })}
              {onDownloadZip ? (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="udv-share-btn"
                    style={{ width: '100%' }}
                    disabled={busy}
                    onClick={() => onDownloadZip()}
                  >
                    <Icon path={mdiDownload} size={0.8} color="currentColor" />
                    <span className="udv-share-btn-label">
                      {t('share.downloadZip')}
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          {zipWarn && !zipBlocked ? (
            <div className="udv-share-warn">
              {t('share.zipWarn', {
                size: zipSizeLabel || formatBytes(SHARE_ZIP_WARN_BYTES),
              })}
            </div>
          ) : null}
        </div>

        <StudyShareLinkSection
          shareUrl={studyShareUrl}
          busy={busy}
          portalMode={portalMode}
          onCopyLink={onCopyStudyLink}
          onShareLink={onShareStudyLink}
        />

        {canPreviews ? (
          <div className="udv-share-section">
            <div className="udv-share-section-label">{t('share.sectionPreviews')}</div>
            <div className="udv-share-actions">
              <button
                type="button"
                className={`udv-share-btn${busy ? ' is-busy' : ''}`}
                disabled={busy}
                onClick={() => onShareSeriesPreviews && onShareSeriesPreviews()}
              >
                <Icon path={mdiImageMultiple} size={0.9} color="currentColor" />
                <span className="udv-share-btn-label">{t('share.seriesPreviews')}</span>
                <span className="udv-share-btn-sub">{t('share.seriesPreviewsSub')}</span>
              </button>
              <button
                type="button"
                className={`udv-share-btn${busy ? ' is-busy' : ''}`}
                disabled={busy}
                onClick={() => onShareStudyPreviews && onShareStudyPreviews()}
              >
                <Icon path={mdiImageMultiple} size={0.9} color="currentColor" />
                <span className="udv-share-btn-label">{t('share.studyPreviews')}</span>
                <span className="udv-share-btn-sub">{t('share.studyPreviewsSub')}</span>
              </button>
            </div>
          </div>
        ) : null}

        {canPacs ? (
          <div className="udv-share-section">
            <div className="udv-share-section-label">{t('share.sectionPacs')}</div>
            {Array.isArray(pacsDestOptions) && pacsDestOptions.length ? (
              <div style={{ marginBottom: 8 }}>
                <label
                  className="udv-share-btn-sub"
                  style={{ display: 'block', marginBottom: 4 }}
                >
                  {t('share.destLabel')}
                </label>
                <select
                  className="udv-share-select"
                  disabled={busy}
                  value={pacsDestId || 'default'}
                  onChange={(e) =>
                    onPacsDestChange && onPacsDestChange(e.target.value)
                  }
                >
                  {pacsDestOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {onManageScpDest ? (
                  <button
                    type="button"
                    className="udv-share-link"
                    disabled={busy}
                    onClick={() => onManageScpDest()}
                  >
                    {t('share.destManage')}
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="udv-share-actions">
              <button
                type="button"
                className={`udv-share-btn${busy ? ' is-busy' : ''}`}
                disabled={busy}
                onClick={() => onStowCurrent && onStowCurrent()}
              >
                <Icon path={mdiHospitalBox} size={0.9} color="currentColor" />
                <span className="udv-share-btn-label">{t('share.stowSop')}</span>
                <span className="udv-share-btn-sub">{t('share.stowSopSub')}</span>
              </button>
              <button
                type="button"
                className={`udv-share-btn${busy ? ' is-busy' : ''}`}
                disabled={busy}
                onClick={() => onStowSeries && onStowSeries()}
              >
                <Icon path={mdiHospitalBox} size={0.9} color="currentColor" />
                <span className="udv-share-btn-label">{t('share.stowSeries')}</span>
                <span className="udv-share-btn-sub">{t('share.stowSeriesSub')}</span>
              </button>
            </div>
          </div>
        ) : null}

        {pwaInstallHint ? (
          <div className="udv-share-pwa-hint">
            <p className="udv-share-pwa-hint-text">{getPwaInstallShareHint(t)}</p>
            <div className="udv-share-pwa-hint-actions">
              {typeof onInstallPwa === 'function' ? (
                <button
                  type="button"
                  className="udv-share-pwa-hint-install"
                  disabled={busy}
                  onClick={() => onInstallPwa()}
                >
                  {t('pwa.install')}
                </button>
              ) : null}
              {typeof onDismissPwaInstall === 'function' ? (
                <button
                  type="button"
                  className="udv-share-pwa-hint-dismiss"
                  disabled={busy}
                  onClick={() => onDismissPwaInstall()}
                >
                  {t('pwa.installDismiss')}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="udv-share-status">{status || t('share.hint')}</div>
      </div>
    </div>
  )

  return createPortal(node, document.body)
}

export default ShareSheet
